import { type ShouldBlockFn, useBlocker } from '@tanstack/react-router';
import { DiscardChangesDialog } from '@/components/custom/discard-changes-dialog';

/** Optional copy overrides for the discard prompt. Lets a caller swap in
 *  domain-specific wording (e.g. "empty rows won't be saved") instead of the
 *  generic dirty-form message. */
interface GuardCopy {
  title?: string;
  description?: string;
}

/**
 * Router-level counterpart to {@link useUnsavedChangesGuard} (which guards modal
 * form sheets). For full-page, route-based editors it blocks in-app navigation
 * away from a dirty form — back/forward, sidebar links, and programmatic
 * `navigate()` all funnel through TanStack Router's `useBlocker` and prompt to
 * discard before leaving.
 *
 * `shouldBlockFn` is read live on every navigation attempt, so the caller can
 * flip a ref (e.g. `savedRef.current = true` in a mutation's `onSuccess`) to let
 * the post-save redirect through without a discard prompt — even though the form
 * is still technically dirty at that instant. It receives the `{ current, next }`
 * locations, so a caller can scope the block to genuine page-leaves and let
 * same-route changes (e.g. search-param navigation) through unblocked.
 */
export function useUnsavedRouteGuard(
  shouldBlockFn: ShouldBlockFn,
  copy?: GuardCopy,
  /** Whether to also arm the browser's native "Leave site?" prompt for hard
   *  unloads (tab close / refresh), where `shouldBlockFn` can't run because
   *  there's no in-app destination. Defaults to TanStack's `true` (always
   *  prompt while mounted). Pass a function to gate it on live state — e.g. a
   *  high-traffic page should only prompt when there's genuinely unsaved work,
   *  not on every refresh. */
  enableBeforeUnload?: boolean | (() => boolean),
): {
  blockerDialog: React.ReactElement;
} {
  const blocker = useBlocker({ shouldBlockFn, enableBeforeUnload, withResolver: true });

  const blockerDialog = (
    <DiscardChangesDialog
      open={blocker.status === 'blocked'}
      onKeepEditing={() => {
        if (blocker.status === 'blocked') blocker.reset();
      }}
      onDiscard={() => {
        if (blocker.status === 'blocked') blocker.proceed();
      }}
      title={copy?.title}
      description={copy?.description}
    />
  );

  return { blockerDialog };
}
